import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import { BRAND } from '../config/branding';
import SettingsPanel from '../components/SettingsPanel';

function UserProfile() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [mainTab, setMainTab] = useState('settings');
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  // MFA State
  const [mfaStatus, setMfaStatus] = useState({ is_enabled: false, has_backup_codes: false });
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [showDisableMfa, setShowDisableMfa] = useState(false);
  const [disableMfaPassword, setDisableMfaPassword] = useState('');
  const [mfaQrCode, setMfaQrCode] = useState(null);

  const getProfessionalRoleName = (role) => {
    const roleMap = {
      'third_party_admin': 'Certification Administrator',
      'super_admin': 'System Administrator',
      'client': 'Client User'
    };
    return roleMap[role] || role?.replace('_', ' ');
  };

  useEffect(() => {
    fetchMfaStatus();
    fetchMfaQr();
  }, []);

  const fetchMfaQr = async () => {
    try {
      const response = await axiosClient.get('/mfa/qr');
      setMfaQrCode(response.data.qr_code);
    } catch (err) {
      console.error('Failed to fetch MFA QR code:', err);
    }
  };

  const fetchMfaStatus = async () => {
    try {
      const response = await axiosClient.get('/mfa/status');
      setMfaStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch MFA status:', err);
    }
  };

  const handleSetupMfa = async () => {
    try {
      const response = await axiosClient.post('/mfa/setup', {});
      setMfaSetupData(response.data);
      setShowMfaSetup(true);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to setup MFA' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleVerifyMfaSetup = async () => {
    try {
      const response = await axiosClient.post('/mfa/verify-setup', { code: verificationCode });

      setMessage({ type: 'success', text: 'Two-Factor Authentication enabled successfully!' });
      setVerificationCode('');
      setShowMfaSetup(false);
      fetchMfaStatus();
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Invalid verification code' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleDisableMfa = async () => {
    try {
      await axiosClient.post('/mfa/disable', { code: disableMfaPassword });
      setMessage({ type: 'success', text: 'Two-Factor Authentication disabled successfully' });
      setShowDisableMfa(false);
      setDisableMfaPassword('');
      fetchMfaStatus();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Invalid MFA code' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const downloadBackupCodes = () => {
    if (!mfaSetupData?.backup_codes) return;

    const content = `${BRAND.name} - Backup Codes for ${user.email}\n\n` +
                   `IMPORTANT: Store these codes securely. Each code can only be used once.\n\n` +
                   mfaSetupData.backup_codes.join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `backup_codes_${user.username}.txt`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleExportMyData = async () => {
    try {
      const response = await axiosClient.get(`/users/${user.id}/export-data`, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `my_data_${user.username}_${user.id}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setMessage({ type: 'success', text: 'Your data has been exported successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to export data' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleDeactivateMyAccount = async () => {
    try {
      await axiosClient.post(`/users/${user.id}/deactivate`, { reason: deactivateReason || 'Self-deactivation' });

      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      window.location.href = '/login';
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to deactivate account' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleDeleteMyAccount = async () => {
    if (deleteConfirmation !== 'DELETE_MY_ACCOUNT') {
      setMessage({ type: 'error', text: 'Please type DELETE_MY_ACCOUNT to confirm' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    try {
      await axiosClient.delete(`/users/${user.id}/delete-account?confirm=DELETE_MY_ACCOUNT`);

      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      window.location.href = '/login';
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete account' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, color: '#1a1a1a', fontSize: '32px', fontWeight: '700' }}>Main Settings</h1>
        <p style={{ color: '#666', marginTop: '8px', fontSize: '14px' }}>
          Manage your account, profile, and security preferences
        </p>
      </div>

      {/* Main Tab Switcher */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '30px', borderBottom: '2px solid #e0e0e0' }}>
        <button
          onClick={() => setMainTab('settings')}
          style={{
            padding: '12px 24px', fontSize: '14px', fontWeight: '600', border: 'none', cursor: 'pointer',
            background: 'transparent', color: mainTab === 'settings' ? '#d4af37' : '#666',
            borderBottom: mainTab === 'settings' ? '3px solid #d4af37' : '3px solid transparent',
            marginBottom: '-2px'
          }}
        >
          Edit Profile
        </button>
        <button
          onClick={() => setMainTab('security')}
          style={{
            padding: '12px 24px', fontSize: '14px', fontWeight: '600', border: 'none', cursor: 'pointer',
            background: 'transparent', color: mainTab === 'security' ? '#d4af37' : '#666',
            borderBottom: mainTab === 'security' ? '3px solid #d4af37' : '3px solid transparent',
            marginBottom: '-2px'
          }}
        >
          Security & Account
        </button>
      </div>

      {/* Edit Profile Tab - Full Settings Panel */}
      {mainTab === 'settings' && <SettingsPanel />}

      {/* Security & Account Tab - Original content below */}
      {mainTab === 'security' && (<>

      {/* Message */}
      {message.text && (
        <div style={{
          background: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
          padding: '15px',
          borderRadius: '6px',
          marginBottom: '20px',
          border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
          fontSize: '14px'
        }}>
          {message.text}
        </div>
      )}

      {/* Account Information */}
      <div style={{
        background: 'white',
        border: '2px solid #d4af37',
        borderRadius: '8px',
        padding: '30px',
        marginBottom: '30px'
      }}>
        <h2 style={{ marginTop: 0, color: '#1a1a1a', fontSize: '20px', fontWeight: '700', marginBottom: '25px' }}>
          Account Information
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '600' }}>
              Full Name
            </label>
            <div style={{ fontSize: '16px', color: '#1a1a1a', fontWeight: '500', padding: '12px', background: '#f8f8f8', borderRadius: '4px' }}>
              {user.full_name || 'Not set'}
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '600' }}>
              Email Address
            </label>
            <div style={{ fontSize: '16px', color: '#1a1a1a', fontWeight: '500', padding: '12px', background: '#f8f8f8', borderRadius: '4px' }}>
              {user.email}
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '600' }}>
              Username
            </label>
            <div style={{ fontSize: '16px', color: '#1a1a1a', fontWeight: '500', padding: '12px', background: '#f8f8f8', borderRadius: '4px' }}>
              {user.username}
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '600' }}>
              Role
            </label>
            <div style={{ fontSize: '16px', color: '#1a1a1a', fontWeight: '500', padding: '12px', background: '#f8f8f8', borderRadius: '4px' }}>
              {getProfessionalRoleName(user.role)}
            </div>
          </div>
        </div>
      </div>

      {/* Security Settings */}
      <div style={{
        background: 'white',
        border: '2px solid #d4af37',
        borderRadius: '8px',
        padding: '30px',
        marginBottom: '30px'
      }}>
        <h2 style={{ marginTop: 0, color: '#1a1a1a', fontSize: '20px', fontWeight: '700', marginBottom: '25px' }}>
          Security Settings
        </h2>

        {/* Two-Factor Authentication */}
        <div style={{ marginBottom: '30px', paddingBottom: '30px', borderBottom: '1px solid #eee' }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#1a1a1a', fontSize: '16px', fontWeight: '600' }}>
            Two-Factor Authentication
          </h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
            Add an extra layer of security to your account with time-based one-time passwords (TOTP).
          </p>

          {mfaStatus.is_enabled ? (
            <div>
              <div style={{
                background: '#d4edda',
                border: '1px solid #c3e6cb',
                borderRadius: '4px',
                padding: '15px',
                marginBottom: '15px',
                color: '#155724',
                fontSize: '14px'
              }}>
                Two-Factor Authentication is currently enabled on your account
              </div>
              <button
                onClick={() => setShowDisableMfa(true)}
                style={{
                  padding: '10px 20px',
                  background: '#999',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Disable Two-Factor Authentication
              </button>
            </div>
          ) : (
            <button
              onClick={handleSetupMfa}
              style={{
                padding: '12px 24px',
                background: '#d4af37',
                color: '#1a1a1a',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                width: '100%'
              }}
            >
              Enable Two-Factor Authentication
            </button>
          )}
        </div>

        {/* Data Export */}
        <div style={{ marginBottom: '30px', paddingBottom: '30px', borderBottom: '1px solid #eee' }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#1a1a1a', fontSize: '16px', fontWeight: '600' }}>
            Data Privacy
          </h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
            You have the right to access and control your personal data under GDPR regulations. Download all your information here.
          </p>

          <button
            onClick={handleExportMyData}
            style={{
              padding: '12px 24px',
              background: '#d4af37',
              color: '#1a1a1a',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              width: '100%'
            }}
          >
            Export My Data
          </button>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '10px', margin: 0 }}>
            Download all your personal information and data stored in the system (GDPR Article 15)
          </p>
        </div>

        {/* Account Management */}
        <div>
          <h3 style={{ margin: '0 0 20px 0', color: '#1a1a1a', fontSize: '16px', fontWeight: '600' }}>
            Account Management
          </h3>

          <div style={{ display: 'grid', gap: '15px' }}>
            <div>
              <button
                onClick={() => setShowDeactivateModal(true)}
                style={{
                  padding: '12px 24px',
                  background: '#FFA500',
                  color: '#1a1a1a',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  width: '100%'
                }}
              >
                Deactivate My Account
              </button>
              <p style={{ fontSize: '12px', color: '#999', marginTop: '8px', margin: 0 }}>
                Temporarily suspend your account. Your data will be preserved and can be restored by an administrator.
              </p>
            </div>

            <div>
              <button
                onClick={() => setShowDeleteModal(true)}
                style={{
                  padding: '12px 24px',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  width: '100%'
                }}
              >
                Permanently Delete My Account
              </button>
              <p style={{ fontSize: '12px', color: '#999', marginTop: '8px', margin: 0 }}>
                Permanently delete your account and all associated data (GDPR Article 17 - Right to be Forgotten)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding: '20px',
        textAlign: 'center',
        color: '#999',
        fontSize: '12px',
        borderTop: '1px solid #eee'
      }}>
        <p style={{ margin: 0 }}>{BRAND.name} Main Settings</p>
        <p style={{ margin: '8px 0 0 0' }}>For support, please contact {BRAND.short} administration</p>
      </div>

      </>)}
      {/* END Security & Account Tab */}

      {/* Deactivate Modal */}
      {showDeactivateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, color: '#1a1a1a', fontSize: '20px' }}>Deactivate My Account</h2>
            <p style={{ color: '#666', fontSize: '14px' }}>
              Your account will be suspended and you won't be able to log in. Your data will be preserved and can be restored by an administrator.
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#495057', fontWeight: '600' }}>
                Reason for deactivation (optional)
              </label>
              <textarea
                value={deactivateReason}
                onChange={(e) => setDeactivateReason(e.target.value)}
                placeholder="Why are you deactivating your account?"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d4af37',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minHeight: '80px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeactivateModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#999',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeactivateMyAccount}
                style={{
                  padding: '10px 20px',
                  background: '#d4af37',
                  color: '#1a1a1a',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Deactivate Account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, color: '#dc3545', fontSize: '20px' }}>Delete My Account</h2>
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <strong style={{ color: '#856404', fontSize: '14px' }}>Warning - This action cannot be undone:</strong>
              <ul style={{ margin: '10px 0 0 20px', color: '#856404', fontSize: '14px', paddingLeft: 0 }}>
                <li>All your data will be permanently deleted</li>
                <li>Your account cannot be recovered</li>
                <li>You will be immediately logged out</li>
              </ul>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#495057', fontWeight: '600' }}>
                Type DELETE_MY_ACCOUNT to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="DELETE_MY_ACCOUNT"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d4af37',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation('');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#999',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteMyAccount}
                disabled={deleteConfirmation !== 'DELETE_MY_ACCOUNT'}
                style={{
                  padding: '10px 20px',
                  background: deleteConfirmation === 'DELETE_MY_ACCOUNT' ? '#dc3545' : '#ddd',
                  color: deleteConfirmation === 'DELETE_MY_ACCOUNT' ? 'white' : '#999',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: deleteConfirmation === 'DELETE_MY_ACCOUNT' ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Permanently Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MFA Setup Modal */}
      {showMfaSetup && mfaSetupData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          overflow: 'auto'
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0, color: '#1a1a1a', fontSize: '20px' }}>Setup Two-Factor Authentication</h2>

            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ fontSize: '16px', color: '#1a1a1a', marginBottom: '10px', fontWeight: '600' }}>Step 1: Scan QR Code</h3>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                Use an authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.) to scan this QR code:
              </p>
              <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                <img
                  src={mfaSetupData.qr_code || mfaQrCode}
                  alt="MFA QR Code"
                  style={{ maxWidth: '200px', border: '2px solid #d4af37', borderRadius: '8px' }}
                />
              </div>
              <div style={{ background: '#f8f8f8', padding: '12px', borderRadius: '4px', textAlign: 'center', border: '1px solid #d4af37' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                  Or enter this code manually:
                </div>
                <code style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a', letterSpacing: '2px' }}>
                  {mfaSetupData.secret}
                </code>
              </div>
            </div>

            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ fontSize: '16px', color: '#1a1a1a', marginBottom: '10px', fontWeight: '600' }}>Step 2: Verify Setup</h3>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                Enter the 6-digit code from your authenticator app:
              </p>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength="6"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d4af37',
                  borderRadius: '4px',
                  fontSize: '18px',
                  textAlign: 'center',
                  letterSpacing: '8px',
                  fontFamily: 'monospace',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {mfaSetupData.backup_codes && (
              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ fontSize: '16px', color: '#1a1a1a', marginBottom: '10px', fontWeight: '600' }}>Step 3: Save Backup Codes</h3>
                <div style={{
                  background: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '4px',
                  padding: '15px',
                  marginBottom: '15px'
                }}>
                  <strong style={{ color: '#856404', fontSize: '14px' }}>Important:</strong>
                  <p style={{ margin: '10px 0 0 0', color: '#856404', fontSize: '14px' }}>
                    Save these backup codes securely. Each code can only be used once to access your account if you lose your authenticator device.
                  </p>
                </div>
                <div style={{
                  background: '#f8f8f8',
                  padding: '15px',
                  borderRadius: '4px',
                  marginBottom: '10px',
                  maxHeight: '150px',
                  overflow: 'auto',
                  border: '1px solid #d4af37'
                }}>
                  {mfaSetupData.backup_codes.map((code, idx) => (
                    <div key={idx} style={{
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      padding: '5px',
                      borderBottom: idx < mfaSetupData.backup_codes.length - 1 ? '1px solid #eee' : 'none'
                    }}>
                      {idx + 1}. {code}
                    </div>
                  ))}
                </div>
                <button
                  onClick={downloadBackupCodes}
                  style={{
                    padding: '10px 20px',
                    background: '#d4af37',
                    color: '#1a1a1a',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    width: '100%'
                  }}
                >
                  Download Backup Codes
                </button>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowMfaSetup(false);
                  setMfaSetupData(null);
                  setVerificationCode('');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#999',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyMfaSetup}
                disabled={verificationCode.length !== 6}
                style={{
                  padding: '10px 20px',
                  background: verificationCode.length === 6 ? '#d4af37' : '#ddd',
                  color: verificationCode.length === 6 ? '#1a1a1a' : '#999',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: verificationCode.length === 6 ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Verify & Enable
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disable MFA Modal */}
      {showDisableMfa && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, color: '#dc3545', fontSize: '20px' }}>Disable Two-Factor Authentication</h2>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
              To disable two-factor authentication, please enter your account password to confirm:
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#495057', fontWeight: '600' }}>
                Password
              </label>
              <input
                type="password"
                value={disableMfaPassword}
                onChange={(e) => setDisableMfaPassword(e.target.value)}
                placeholder="Enter your password"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d4af37',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDisableMfa(false);
                  setDisableMfaPassword('');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#999',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDisableMfa}
                disabled={!disableMfaPassword}
                style={{
                  padding: '10px 20px',
                  background: disableMfaPassword ? '#dc3545' : '#ddd',
                  color: disableMfaPassword ? 'white' : '#999',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: disableMfaPassword ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Disable Two-Factor Authentication
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserProfile;
